import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import { getAllUsers } from '../../api/userAPI';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const AnnouncementForm = ({ open, handleClose, handleAddSubmit, formData, handleChange, handleUsersChange, isEdit }) => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (open) {
      getAllUsers()
        .then((res) => {
          const officeUsers = res.data.filter(u => u.isOfficeEmp);
          setUsers(officeUsers.length > 0 ? officeUsers : res.data);
        })
        .catch((err) => console.error(err));
    }
  }, [open]);

  const isAllSelected = users.length > 0 && formData.assignedUsers.length === users.length;

  const onUsersChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === "all") {
      if (isAllSelected) {
         handleUsersChange({ target: { name: 'assignedUsers', value: [] }});
      } else {
         handleUsersChange({ target: { name: 'assignedUsers', value: users.map(u => u._id || u.id) }});
      }
      return;
    }
    handleUsersChange(event);
  };

  return (
    <Drawer 
      anchor="right"
      open={open} 
      onClose={handleClose} 
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          p: { xs: 2, sm: 4 },
          boxShadow: '-4px 0 24px rgba(0,0,0,0.1)'
        }
      }}
    >
      <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <Typography variant="h5" fontWeight="bold" sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #9c27b0 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {isEdit ? "✨ Update Announcement" : "✨ Create New Announcement"}
        </Typography>
      </Box>
      <form onSubmit={handleAddSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            margin="dense"
            label="Message"
            type="text"
            fullWidth
            variant="outlined"
            name="message"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleChange}
            required
            sx={{ mb: 3 }}
          />
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="assigned-users-label">Assign To</InputLabel>
            <Select
              labelId="assigned-users-label"
              id="assigned-users"
              multiple
              value={formData.assignedUsers}
              onChange={onUsersChange}
              input={<OutlinedInput label="Assign To" />}
              renderValue={(selected) => {
                if (selected.length === users.length && users.length > 0) return "All Users";
                const selectedUsers = users.filter(user => selected.includes(user._id || user.id));
                return selectedUsers.map(user => user.fullName).join(', ');
              }}
              MenuProps={MenuProps}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={formData.assignedUsers.length > 0 && formData.assignedUsers.length < users.length}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id || user.id} value={user._id || user.id}>
                  <Checkbox checked={formData.assignedUsers.indexOf(user._id || user.id) > -1} />
                  <ListItemText primary={user.fullName} secondary={user.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Button 
            onClick={handleClose} 
            sx={{ 
              color: '#6c757d', 
              fontWeight: 600,
              '&:hover': { background: 'rgba(0,0,0,0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #9c27b0 90%)',
              boxShadow: '0 4px 14px 0 rgba(156, 39, 176, 0.39)',
              borderRadius: '8px',
              px: 4,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #7b1fa2 90%)',
              }
            }}
          >
            {isEdit ? "Save Changes" : "Publish Announcement"}
          </Button>
        </Box>
      </form>
    </Drawer>
  );
};

export default AnnouncementForm;
